import { init as _initMito } from '@mitojs/browser'
import { qsStringify } from '@voghion/fe-util'
import { Router } from 'vue-router'
import { getCurrentSiteConfig } from './site-config'
import { getUserConsent, UserConsentResult } from '@voghion/native-api'
import { AxiosError, AxiosResponse } from 'axios'

interface Exception extends Record<string, any> {
  description?: string
}

interface EventParams extends Record<string, any> {}

interface Event {
  /**
   * 事件名
   */
  event: string
  /**
   * 参数
   */
  params: EventParams
  /**
   * 是否是 gtm 事件
   */
  gtm?: boolean
  /**
   * 是否是 facebook 事件
   */
  fbq?: boolean
  /**
   * 操作
   */
  action?: string
}

/**
 * 错误类型
 */
const ERROR_TYPE_API = 'API' // 服务器的 api 错误
const ERROR_TYPE_VENDOR_API = 'VENDOR_API' // 第三方的 api 错误
const ERROR_TYPE_IMAGE_COMPRESS = 'IMAGE_COMPRESS' // 图片压缩错误

/**
 * 网络错误
 */
const networkErrorMessages = ['Failed to fetch', 'NetworkError when attempting to fetch resource.', 'Network Error']

/**
 * 初始化选项
 */
type InitOptions = {
  isProd?: boolean // 是否是正式环境
  router?: Router // vue-router 实例，如果传入，会在每次路由结束后上报 page_view 事件
  userId?: string // 埋点用户标识
  deviceCode: string // 埋点设备号
  needConsent?: boolean // 是否需要确认隐私协议
}

let inited = false
let latestOptions: InitOptions
let routePath = '/'
let routeQuery = ''
let currentPageName = ''
let latestUserConsentResult: UserConsentResult
let consentReady = false // consent 是否已就绪（用户做出选择）

/**
 * 是否开启埋点
 * @returns
 */
export const useAnalyticsEnable = function () {
  return !!latestOptions.isProd
}

/**
 * 初始化埋点
 * @param options
 */
export function initAnalytics(options: InitOptions) {
  const siteConfig = getCurrentSiteConfig()
  latestOptions = {
    ...options,
    needConsent: siteConfig.needConsent
  }
  if (!inited) {
    inited = true
    setDataLayer({
      user_id: String(options.userId || ''),
      device_code: options.deviceCode
    })
    const router = options.router
    if (router) {
      router.afterEach(function (to) {
        routePath = getVueRoutePath(router)
        routeQuery = getVueRouteQuery(router)
        const dataLayer = (window.dataLayer = window.dataLayer ?? [])
        const pageName = (to.meta?.pageName || 'coupon_page') as string
        currentPageName = pageName
        dataLayer.push({
          routePath,
          routeQuery,
          page_name: pageName
        })
        const pageViewManual = !!to.meta.pageViewManual
        if (!pageViewManual) {
          setTimeout(() => {
            trackPageView()
          })
        }
      })
    }
    if (process.env.NODE_ENV === 'production') {
      if (!!latestOptions.isProd) {
        initMito()
      }
      initGtm(siteConfig.gtmId)
      initFacebookPixel(siteConfig.facebookPixelId)
    }
  }
  if (!consentReady) {
    if (!latestOptions.needConsent) {
      consentReady = true
    } else {
      getUserConsent({}).then((res) => {
        if (res.data) {
          confirmConsent(res.data)
        } else {
          confirmConsent({
            ad: true,
            analytics: true
          })
        }
      })
    }
  }
}

/**
 * 确认数据相关的协议
 */
export function confirmConsent(result: UserConsentResult) {
  latestUserConsentResult = result
  consentReady = true
  grantConsentGTM()
  setTimeout(() => {
    grantConsentFacebookPixel()
  }, 100)
}

function grantConsentFacebookPixel() {
  if (latestUserConsentResult && window.fbq) {
    if (latestUserConsentResult.ad && latestUserConsentResult.analytics) {
      window.fbq('consent', 'grant')
    }
  }
}

/**
 * 初始化 gtm
 * @param gtmId
 */
function initGtm(gtmId: string) {
  ;(function (w, d, s, l, i) {
    w[l] = w[l] || []
    w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
    var f = d.getElementsByTagName(s)[0],
      j = d.createElement(s) as HTMLScriptElement,
      dl = l != 'dataLayer' ? '&l=' + l : ''
    j.async = true
    j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl
    f.parentNode.insertBefore(j, f)
  })(window, document, 'script', 'dataLayer', gtmId)
}

/**
 * 初始化 facebook pixel
 * @param facebookPixelId
 * @returns
 */
function initFacebookPixel(facebookPixelId: string) {
  if (!facebookPixelId) {
    return
  }
  !(function (f: Window, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) {
      return
    }
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    }
    if (!f._fbq) {
      f._fbq = n
    }
    n.push = n
    n.loaded = !0
    n.version = '2.0'
    n.queue = []
    t = b.createElement(e)
    t.async = !0
    t.src = v
    s = b.getElementsByTagName(e)[0]
    s.parentNode.insertBefore(t, s)
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js')
  if (latestOptions.needConsent) {
    window.fbq('consent', 'revoke')
  }
  window.fbq('init', facebookPixelId)
  window.fbq('track', 'PageView')
}

function grantConsentGTM() {
  const consentValue: Record<string, string> = {}
  if (latestUserConsentResult) {
    let needUpdate = false
    if (latestUserConsentResult.ad) {
      consentValue.ad_storage = 'granted'
      consentValue.ad_user_data = 'granted'
      consentValue.ad_personalization = 'granted'
      needUpdate = true
    }
    if (latestUserConsentResult.analytics) {
      consentValue.analytics_storage = 'granted'
      needUpdate = true
    }
    if (needUpdate) {
      gtag('consent', 'update', consentValue)
    }
  }
}

/**
 * 初始化 mitojs
 */
function initMito() {
  _initMito({
    debug: false,
    dsn: '',
    // 堆栈太大会超过 ga 64kb 的限制
    maxBreadcrumbs: 5,
    beforeDataReport(event) {
      if (event && event.data) {
        const data = event.data
        if (data.type === 'HTTP') {
          const request = data.request
          const response = data.response
          if (request && response) {
            const params: any = {
              request_url: request.url,
              request_data: request.data || '',
              request_method: request.method,
              response_data: response.data,
              response_status: response.status,
              description: data.message
            }
            if (request.url?.includes('voghion.')) {
              // 项目内的请求都是通过 ofetch 发起的，ofetch 通过闭包引用了原始的 fetch，所以 mito 无法通过重写 fetch 监听请求
            } else if (!request.url?.includes('googleapis.com')) {
              // firebase 安装错误不上报，大概率都是网络引起的超时
              params.error_type = ERROR_TYPE_VENDOR_API
              logException(params)
            }
          }
        } else if (data.type === 'RESOURCE') {
          if (data.message) {
            logException({
              error_type: data.type,
              description: data.message.replace('资源地址: ', '')
            })
          }
        } else if (data.type) {
          const params: Exception = {
            error_type: data.type,
            description: data.message || ''
          }
          if (data.stack) {
            addStringValueToParams(params, 'stack', data.stack)
          }
          logException(params)
        }
      }
      return false
    }
  })
}

function getVueRoutePath(router: Router) {
  const baseStr = router.options.history.base
  const base: string = baseStr.includes('#') ? baseStr.split('#')[1] : baseStr
  const currentRoute = router.currentRoute
  const path = currentRoute.value?.path || '/'
  return base + path
}

function getVueRouteQuery(router: Router) {
  const currentRoute = router.currentRoute
  return qsStringify(currentRoute.value?.query || {})
}

/**
 * 将数据写入 dataLayer
 * @param params
 */
export function setDataLayer(params: Record<string, any> = {}) {
  const dataLayer = (window.dataLayer = window.dataLayer ?? [])
  dataLayer.push(params)
}

/**
 * gtag 命令
 * @param params
 */
export function gtag(..._params: any[]) {
  // eslint-disable-next-line prefer-rest-params
  setDataLayer(arguments)
}

function innerTrackFBQEvent(action: string, event: string, params: EventParams = {}) {
  window.fbq(action, event, params)
}

/**
 * 触发 facebook 事件
 * @param {*} event
 * @param {*} params
 */
export function trackFBQEvent(action: string, event: string, params: EventParams = {}) {
  if (consentReady) {
    innerTrackFBQEvent(action, event, params)
  } else {
    eventQueue.push({
      event,
      params,
      action,
      fbq: true
    })
    if (!eventLoopTimer) {
      eventLoopTimer = setInterval(() => {
        trackEventFromQueue()
      }, 1000)
    }
  }
}

function innerTrackGTMEvent(event: string, params: EventParams = {}) {
  if (params.ecommerce) {
    // 如果是电商事件，先清除之前的电商对象
    setDataLayer({
      ecommerce: null
    })
  }
  setDataLayer({
    event,
    ...params
  })
}

/**
 * 触发 gtm 事件
 * @param action
 * @param event
 * @param params
 */
export function trackGTMEvent(action: string, event: string, params = {}) {
  if (consentReady) {
    innerTrackGTMEvent(event, params)
  } else {
    eventQueue.push({
      event,
      params,
      action,
      gtm: true
    })
    if (!eventLoopTimer) {
      eventLoopTimer = setInterval(() => {
        trackEventFromQueue()
      }, 1000)
    }
  }
}

// 上报事件到 gtm，通过 datalayer
export function logEvent(eventName: string, params: any = {}) {
  setDataLayer({
    event: eventName,
    ...params
  })
}

// 上报 api 错误
export function logApiError(params: Exception) {
  params.error_type = ERROR_TYPE_API
  logException(params)
}

// 上报图片压缩错误
export function logImageCompressError(params: Exception) {
  params.error_type = ERROR_TYPE_IMAGE_COMPRESS
  logException(params)
}

let gaMeasurementId = '' // 衡量 id 在一个页面中只能引入一个
const eventQueue: Event[] = [] // 异常上报队列

/**
 * 从 gtm 全局变量中获取 ga 衡量id
 */
function getGAMeasurementId() {
  if (!gaMeasurementId) {
    if (window.google_tag_manager) {
      const keys = Object.keys(window.google_tag_manager)
      for (const key of keys) {
        if (key.startsWith('G-')) {
          gaMeasurementId = key
          break
        }
      }
    }
  }
}

let eventLoopTimer: NodeJS.Timeout | number
/**
 * 触发缓存队列中的异常事件
 */
function trackEventFromQueue() {
  getGAMeasurementId()
  if (gaMeasurementId && consentReady && window.google_tag_manager && window.google_tag_manager.mb) {
    if (eventLoopTimer) {
      clearInterval(eventLoopTimer)
      eventLoopTimer = 0
    }
    while (eventQueue.length) {
      const item = eventQueue.shift()
      if (item) {
        if (item.fbq) {
          innerTrackFBQEvent(item.action || '', item.event, item.params)
        } else if (item.gtm) {
          innerTrackGTMEvent(item.event, item.params)
        } else {
          innerTrackGAEvent(item.event, item.params)
        }
      }
    }
  }
}

/**
 * 触发 ga 异常事件
 * @param params
 */
function trackGAException(params: Exception) {
  trackGAEvent('exception', params)
}

/**
 * 触发 ga 事件
 * @param event
 * @param params
 */
function innerTrackGAEvent(event: string, params: Record<string, any> = {}) {
  params.send_to = gaMeasurementId
  if (window.google_tag_manager) {
    window.google_tag_manager.mb.enqueue(
      (function (_cmd: string, _event: string, _params: Record<string, any>) {
        // eslint-disable-next-line prefer-rest-params
        return arguments
      })('event', event, params),
      0,
      {}
    )
  }
}

/**
 * 触发 ga 事件
 * @param event
 * @param params
 */
export function trackGAEvent(event: string, params: Record<string, any> = {}) {
  if (!useAnalyticsEnable()) {
    return console.log({
      event,
      params
    })
  }
  params.page_name = currentPageName
  getGAMeasurementId()
  if (gaMeasurementId && consentReady) {
    trackEventFromQueue()
    innerTrackGAEvent(event, params)
  } else {
    eventQueue.push({
      event,
      params
    })
    if (!eventLoopTimer) {
      eventLoopTimer = setInterval(() => {
        getGAMeasurementId()
        trackEventFromQueue()
      }, 1000)
    }
  }
}

/**
 * 获取 ga 配置，暂时没处理 gtm ready 状态
 * @param key
 * @returns
 */
export function getGAConfig<T = string>(key: string) {
  return new Promise<T>((resolve, reject) => {
    getGAMeasurementId()
    if (gaMeasurementId && window.google_tag_manager) {
      window.google_tag_manager.mb.enqueue(
        (function (_cmd: string, _sendTo: string, _key: string, _callback: CallableFunction) {
          // eslint-disable-next-line prefer-rest-params
          return arguments
        })('get', gaMeasurementId, key, (res: any) => {
          resolve(res)
        }),
        0,
        {}
      )
    } else {
      reject(new Error('ga not ready'))
    }
  })
}

// ga 推荐使用 exception 来记录异常 https://developers.google.com/analytics/devguides/collection/ga4/exceptions?hl=zh-cn
export function logException(params: Exception) {
  params.network_info = getNetworkInfo()
  if (params.description && networkErrorMessages.includes(params.description)) {
    params.network_error = true
  }
  if (!('fatal' in params)) {
    params.fatal = false
  }
  /**
   * 异常通过 gtm 直接上报到 ga，google 并没有公开相关的 api，通过调试 gtm.js 找到的
   *
   * 在 gtm 后台去配置异常上报有两个问题
   * 1. 错误信息没办法像 ecommerce 那样放在一个对象里，然后重置
   * 2. 部分错误信息字段会超过 100 个字符（超过了 ga 的限制），当拆分后导致后台配置事件属性很麻烦
   *
   * 流程
   * 1. 找到 gtm 引入的 ga measurementId
   * 2. 如果 gtag 已初始化，通过 gtm 内部 api 上报
   * 3. 如果 gtag 未初始化，放入队列
   */
  trackGAException(params)
}

/**
 * 获取网络信息
 * @returns
 */
function getNetworkInfo() {
  if ('connection' in navigator) {
    const connection = navigator.connection as any
    return JSON.stringify({
      downlink: connection.downlink,
      effectiveType: connection.effectiveType,
      rtt: connection.rtt,
      saveData: connection.saveData
    })
  } else {
    return ''
  }
}

/**
 * 添加自定义字符串属性，目前用的 ga 收集自定义属性，每个字段有 100 的长度限制，当超过100时，拆分为多个字段，最多20个
 * @param {*} params
 * @param {*} key
 * @param {*} value
 */
export function addStringValueToParams(params: Record<string, string>, key: string, value: string) {
  if (value.length <= 100) {
    params[key] = value
  } else {
    const splitLen = Math.min(Math.ceil(value.length / 100), 20)
    for (let i = 0; i < splitLen; i++) {
      params[`${key}_${i}`] = value.substring(i * 100, (i + 1) * 100)
    }
  }
}

// 上报 axios 错误
export function logAxiosError(response: AxiosResponse | AxiosError) {
  const request = response.request
  let requestUrl = request?.responseURL
  let requestMethod = 'POST'
  let requestData = ''
  if (request && request.httpCollect) {
    const httpCollect = request.httpCollect
    // @mitojs/browser 会重写 xhr 的 open 并添加 httpCollect 对象
    requestUrl = httpCollect.request.url
    requestMethod = httpCollect.request.method
    requestData = httpCollect.request.data || ''
  }
  if (response.config) {
    const config = response.config
    requestMethod = config.method ? config.method.toUpperCase() : 'POST'
    requestData = config.data || ''
  }
  if (typeof requestData === 'object') {
    requestData = JSON.stringify(requestData)
  }
  const params: any = {
    request_url: requestUrl,
    request_method: requestMethod,
    response_status: response.status || 500
  }
  let responseData = ''
  let errorMessage = ''
  if ('stack' in response && response.stack) {
    // 如果是 AxiosError
    errorMessage = response.stack
  } else if ('data' in response) {
    // 如果是接口业务错误
    const data = response.data
    responseData = typeof data === 'object' ? JSON.stringify(data) : data
    errorMessage = typeof data === 'object' ? data.rtn_msg || '' : data
  }
  addStringValueToParams(params, 'request_data', requestData)
  addStringValueToParams(params, 'response_data', responseData)
  addStringValueToParams(params, 'error_message', errorMessage)
  if (params.error_message) {
    params.description = params.error_message
  } else {
    params.description = params.error_message_0
  }
  logApiError(params)
}

/**
 * 触发页面曝光
 */
export function trackPageView() {
  trackGAEvent('page_view')
}
